import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PhotoSlide from '../PhotoSlide';
import { ProductHeroProps } from '../../types';
import { useI18n } from '../../i18n';
import { en, km } from './ProductHero.data';

const FALLBACK_BACKGROUND = '/images/backgrounds/erp-bg.mp4';

const getVideoMimeType = (src: string) => {
  if (src.endsWith('.webm')) {
    return 'video/webm';
  }

  if (src.endsWith('.ogg') || src.endsWith('.ogv')) {
    return 'video/ogg';
  }

  return 'video/mp4';
};

const ProductHero: React.FC<ProductHeroProps> = ({ product }) => {
  const { locale, localePath } = useI18n();
  const t = locale === 'km' ? km : en;
  const backgroundSrc = product.backgroundImage || FALLBACK_BACKGROUND;
  const hasVideoBackground = /\.(mp4|webm|ogg|ogv)(\?.*)?$/i.test(backgroundSrc);
  const hasSlideshow = Boolean(product.images && product.images.length > 1);

  return (
    <HeroSection>
      {hasVideoBackground ? (
        <BackgroundVideo
          key={backgroundSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src={backgroundSrc} type={getVideoMimeType(backgroundSrc)} />
        </BackgroundVideo>
      ) : (
        <BackgroundImage $backgroundImage={backgroundSrc} />
      )}
      <HeroOverlay />
      {product.tagline && <Eyebrow>{product.tagline}</Eyebrow>}
      <HeroTitle>{product.name}</HeroTitle>
      <HeroContent>
        <p>{product.description}</p>
        {product.highlights && product.highlights.length > 0 && (
          <HighlightList>
            {product.highlights.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </HighlightList>
        )}
        <HeroActions>
          <PrimaryButton to={localePath('/contact')}>{t.talkToUs}</PrimaryButton>
          {product.docsUrl && (
            <SecondaryAnchor href={product.docsUrl} target="_blank" rel="noopener noreferrer">
              {t.userGuide}
            </SecondaryAnchor>
          )}
          <GhostLink to={localePath('/demo')}>{t.liveDemos}</GhostLink>
        </HeroActions>
      </HeroContent>
      {hasSlideshow && (
        <SlideshowContainer>
          <PhotoSlide images={product.images || []} />
        </SlideshowContainer>
      )}
      {product.demoUrl && (
        <DemoInfo>
          <p>
            {t.demoVisitPrefix}{' '}
            <a href={product.demoUrl} target="_blank" className="text-blue-500" rel="noopener noreferrer">
              {product.demoUrl}
            </a>
          </p>
          <div>{t.usernameLabel} {product.demoUsername}</div>
          <div>{t.passwordLabel} {product.demoPassword}</div>
        </DemoInfo>
      )}
    </HeroSection>
  );
};

export const ProductContainer = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 2rem 1rem 4.5rem;
`;

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  column-gap: 2.5rem;
  row-gap: 0;
  margin-bottom: 1.5rem;
  align-items: center;
  padding: 3rem;
  position: relative;
  border-radius: 28px;
  overflow: hidden;
  border: 1px solid var(--border);
  background:
    radial-gradient(600px 300px at 92% 0%, rgba(0, 166, 232, 0.14) 0%, transparent 62%),
    linear-gradient(180deg, #f8fcfe 0%, #eef7fc 100%);
  box-shadow: var(--shadow-sm);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    padding: 1.7rem;
    border-radius: 22px;
  }
`;

const BackgroundImage = styled.div<{ $backgroundImage: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $backgroundImage }) => $backgroundImage});
  background-size: cover;
  background-position: center 45%;
  opacity: 0.16;
  z-index: 0;
`;

const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 45%;
  opacity: 0.16;
  z-index: 0;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(247, 251, 254, 0.94) 0%, rgba(247, 251, 254, 0.72) 58%, rgba(247, 251, 254, 0.4) 100%);
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding-top: 1rem;

  > p {
    font-size: 1.05rem;
    color: var(--text-muted);
    line-height: 1.7;
    margin: 0 0 1.2rem;
  }
`;

const HeroTitle = styled.h1`
  grid-column: 1 / -1;
  position: relative;
  z-index: 2;
  font-size: clamp(2rem, 4vw, 2.7rem);
  margin: 0;
  color: var(--text-dark);
  line-height: 1.15;
  letter-spacing: -0.03em;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.85rem;
    white-space: normal;
  }
`;

const Eyebrow = styled.p`
  grid-column: 1 / -1;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.85rem;
  color: #0a6b93;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  line-height: 1.5;
  white-space: nowrap;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: var(--primary-color);
    box-shadow: 0 0 0 4px rgba(0, 142, 204, 0.14);
  }

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

const HighlightList = styled.ul`
  margin: 0 0 1.5rem;
  padding: 0;
  list-style: none;
  color: var(--text-dark);

  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.45rem;
    line-height: 1.55;
    font-size: 0.97rem;
  }

  li::before {
    content: '✓';
    position: absolute;
    left: 0;
    top: 0.05rem;
    color: #17a366;
    font-weight: 800;
    font-size: 0.82rem;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.7rem;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.4rem;
  background: var(--gradient-brand);
  color: white;
  font-weight: 700;
  border-radius: 999px;
  box-shadow: 0 10px 22px rgba(0, 142, 204, 0.26);
  transition: transform 0.25s var(--ease), box-shadow 0.25s var(--ease);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px rgba(0, 142, 204, 0.32);
  }
`;

const SecondaryAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.35rem;
  border: 1.5px solid rgba(0, 142, 204, 0.32);
  background: rgba(255, 255, 255, 0.75);
  color: #0a6b93;
  font-weight: 700;
  border-radius: 999px;
  transition: background 0.22s ease, border-color 0.22s ease;

  &:hover {
    background: white;
    border-color: var(--primary-color);
  }
`;

const GhostLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 0.75rem;
  color: var(--text-dark);
  font-weight: 650;
  transition: color 0.2s ease;

  &:hover {
    color: var(--primary-color);
  }
`;

const SlideshowContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: 2;
`;

const DemoInfo = styled.div`
  position: relative;
  z-index: 2;
  color: var(--text-dark);
  grid-column: 1 / -1;
  margin-top: 1.5rem;

  p {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }

  a {
    color: var(--primary-color);
  }
`;

export default ProductHero;
